import { useEffect, useState } from 'react';

/**
 * Tailwind's `md` breakpoint. Below it the desktop shell's 208px sidebar
 * leaves ~119px of content on a phone, so this is where the mobile shell
 * takes over rather than a device-type sniff — a narrow desktop window gets
 * the same treatment, which is the honest behaviour and is testable.
 */
const MOBILE_QUERY = '(max-width: 767px)';

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const sync = () => setIsMobile(mq.matches);

    // Safari before 14 has no addEventListener on MediaQueryList, only the
    // deprecated addListener. Calling the missing one throws, which would
    // take the whole app shell down on an older iPhone — exactly the device
    // this mobile build exists for.
    const modern = typeof mq.addEventListener === 'function';
    if (modern) mq.addEventListener('change', sync);
    else mq.addListener(sync);

    // Also on resize. The media query event is the precise signal, but it
    // isn't delivered in every environment (notably a devtools viewport
    // override), and a missed one leaves the wrong shell on screen.
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);

    // Re-read on mount in case the width changed between render and effect
    // (rotation during load, or a restored window size).
    sync();

    return () => {
      if (modern) mq.removeEventListener('change', sync);
      else mq.removeListener(sync);
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
    };
  }, []);

  return isMobile;
}
